import { PDFDocument, PDFName, PDFDict, PDFArray, PDFString, PDFHexString } from 'pdf-lib';

// DecompressionStream is available in modern browsers but missing from some TS lib targets
declare const DecompressionStream: {
  new (format: string): { readable: ReadableStream<Uint8Array>; writable: WritableStream<Uint8Array> };
};

const ATTACHMENT_NAME = 'bidflow-annotations.json';
const METADATA_VERSION = 1;

export interface BidflowComponentKey {
  name: string;
  color: string;
  symbol: string;
  symbolType?: string;
  componentType?: string;
  description?: string;
  subscript?: string;
  category?: string;
  laborUnits?: number;
  materialCost?: number;
  referenceImage?: string;
  hasReferenceImage?: boolean;
}

export interface BidflowAnnotation {
  id: string;
  type: string;
  component_type?: string;
  page: number;
  startX: number;
  startY: number;
  endX?: number;
  endY?: number;
  width?: number;
  height?: number;
  text?: string;
  color: string;
  thickness: number;
  points?: { x: number; y: number }[];
  timestamp: string;
  number?: number;
  x?: number;
  y?: number;
  radius?: number;
  startAnnotationId?: string;
  endAnnotationId?: string;
  measurement?: number;
  measurementUnit?: string;
  symbolType?: string;
  componentName?: string;
  wireTypeName?: string;
  wireTypeColor?: string;
  totalLength?: number;
  relativeX?: number;
  relativeY?: number;
  relativeStartX?: number;
  relativeStartY?: number;
  relativeWidth?: number;
  relativeHeight?: number;
  savedCanvasWidth?: number;
  savedCanvasHeight?: number;
  auto_detected?: boolean;
}

export interface BidflowMetadata {
  version: number;
  exportedAt: string;
  projectKey: {
    components: BidflowComponentKey[];
    extractedAt?: string;
    pdfHash?: string;
  };
  annotations: Record<string, BidflowAnnotation[]>;
  referenceImages: Record<string, string>;
  fileId?: string;
  fileName?: string;
}

/**
 * Embed bidflow annotation metadata as a file attachment inside a PDF.
 * Returns the modified PDF as a Uint8Array.
 */
export async function embedMetadataInPDF(
  pdfBytes: ArrayBuffer,
  metadata: BidflowMetadata
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(pdfBytes);

  const jsonStr = JSON.stringify(metadata);
  const jsonBytes = new TextEncoder().encode(jsonStr);

  pdfDoc.attach(jsonBytes, ATTACHMENT_NAME, {
    mimeType: 'application/json',
    description: 'Bidflow annotation metadata',
  });

  return pdfDoc.save();
}

/**
 * Decompress FlateDecode (zlib RFC 1950) data using the browser's DecompressionStream API.
 */
async function inflateBytes(compressed: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate');
  const writer = ds.writable.getWriter();
  writer.write(compressed);
  writer.close();

  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const totalLen = chunks.reduce((sum, c) => sum + c.length, 0);
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

/**
 * Extract bidflow metadata from a PDF's embedded file attachments.
 * Traverses Catalog -> Names -> EmbeddedFiles name tree using pdf-lib internals.
 */
export async function extractMetadataFromPDF(
  pdfBytes: ArrayBuffer
): Promise<BidflowMetadata | null> {
  try {
    const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
    const catalog = pdfDoc.catalog;

    const namesDict = catalog.lookup(PDFName.of('Names'));
    if (!(namesDict instanceof PDFDict)) return null;

    const embeddedFiles = namesDict.lookup(PDFName.of('EmbeddedFiles'));
    if (!(embeddedFiles instanceof PDFDict)) return null;

    const namesArray = embeddedFiles.lookup(PDFName.of('Names'));
    if (!(namesArray instanceof PDFArray)) return null;

    // Names array structure: [name1, filespec1, name2, filespec2, ...]
    for (let i = 0; i < namesArray.size(); i += 2) {
      const nameObj = namesArray.lookup(i);

      let nameStr: string | null = null;
      if (nameObj instanceof PDFHexString) nameStr = nameObj.decodeText();
      else if (nameObj instanceof PDFString) nameStr = nameObj.decodeText();
      if (nameStr !== ATTACHMENT_NAME) continue;

      const fileSpec = namesArray.lookup(i + 1);
      if (!(fileSpec instanceof PDFDict)) continue;

      const efDict = fileSpec.lookup(PDFName.of('EF'));
      if (!(efDict instanceof PDFDict)) continue;

      const stream = efDict.lookup(PDFName.of('F')) as any;
      if (!stream) continue;

      // Get raw stream bytes
      let rawBytes: Uint8Array | undefined;
      if (typeof stream.getContents === 'function') {
        rawBytes = stream.getContents();
      } else if (stream.contents instanceof Uint8Array) {
        rawBytes = stream.contents;
      }
      if (!rawBytes) continue;

      // Check if stream is compressed (FlateDecode)
      const streamDict: PDFDict | undefined = stream.dict;
      const filter = streamDict?.lookup(PDFName.of('Filter'));
      const isFlate = filter instanceof PDFName && filter.toString() === '/FlateDecode';

      let contents: Uint8Array;
      if (isFlate) {
        contents = await inflateBytes(rawBytes);
      } else {
        contents = rawBytes;
      }

      const jsonStr = new TextDecoder().decode(contents);
      const parsed = JSON.parse(jsonStr) as BidflowMetadata;

      if (parsed.version && parsed.projectKey && parsed.annotations) {
        return parsed;
      }
    }

    return null;
  } catch (e) {
    console.error('Failed to extract bidflow metadata from PDF:', e);
    return null;
  }
}

/**
 * Build the metadata payload from the current project state.
 */
export function buildMetadataPayload(opts: {
  projectKey: { components: any[]; extractedAt?: string; pdfHash?: string } | null;
  annotationsByComponent: Record<string, any[]>;
  referenceImages: Map<string, string> | Record<string, string>;
  fileId?: string;
  fileName?: string;
}): BidflowMetadata {
  const { projectKey, annotationsByComponent, referenceImages, fileId, fileName } = opts;

  const refImagesObj: Record<string, string> =
    referenceImages instanceof Map
      ? Object.fromEntries(referenceImages)
      : referenceImages;

  const components: BidflowComponentKey[] = (projectKey?.components || []).map((c: any) => ({
    name: c.name,
    color: c.color,
    symbol: c.symbol || '',
    symbolType: c.symbolType,
    componentType: c.componentType,
    description: c.description || '',
    subscript: c.subscript,
    category: c.category,
    laborUnits: c.laborUnits,
    materialCost: c.materialCost,
    referenceImage: refImagesObj[c.name] || c.referenceImage || '',
    hasReferenceImage: !!(refImagesObj[c.name] || c.referenceImage),
  }));

  return {
    version: METADATA_VERSION,
    exportedAt: new Date().toISOString(),
    projectKey: {
      components,
      extractedAt: projectKey?.extractedAt,
      pdfHash: projectKey?.pdfHash,
    },
    annotations: annotationsByComponent,
    referenceImages: refImagesObj,
    fileId,
    fileName,
  };
}
